
import {
  computed, defineComponent, onMounted, Ref, ref,
} from 'vue';
/* eslint-disable import/no-cycle */
import { useRoute, useRouter } from 'vue-router';
import { formatDate } from '@vueuse/core';
import { useProfileStore } from '../store/profileStore';
import { ACCOUNT_STATUS, APPLICATION_SETTINGS_TYPE, NAVIGATION_PAGES } from '@/types/enums';
import ProfileBottomPopUp from '../components/ProfileBottomPopUp.vue';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import MonitoringService from '@/services/MonitoringService';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import DrivePublicItems from '@/modules/drive/components/DrivePublicItems.vue';

export default defineComponent({
  components: {
    DrivePublicItems,
    ProfileBottomPopUp,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const profileStore = useProfileStore();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const spacesStore = useSpacesStore();

    const isPremium = computed(() => mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.PREMIUM));
    const isDeleted = computed(() => profileStore.foreignProfile.status === 'DELETED');
    const casesCreated = computed(() => profileStore.profile.casesAmount);
    const photosUploaded = computed(() => profileStore.profile.picturesAmount);
    const showPopUp: Ref<boolean> = ref(false);

    const isPublicCases = computed(
      () => mainStore.applicationSettings.find((feature) => feature.name === APPLICATION_SETTINGS_TYPE.SHOW_PUBLIC_CASES_FEED)?.enabled,
    );
    const publicCasesAmount = computed(() => driveStore.userPublicCases.data.length);

    const currentPage = computed(() => route.meta.navigationPage);
    const myProfileById = computed(() => mainStore.user.accountId === route.params.id);
    const isMyProfile = computed(() => currentPage.value === NAVIGATION_PAGES.PROFILE_MY);
    const profile = computed(() => (isMyProfile.value ? profileStore.profile : profileStore.foreignProfile));
    const profileId = computed(() => route.params.id as string);
    const disableButton = computed(() => profileStore.foreignProfile.status === 'REQUEST_SENT');

    const myState = computed(() => profileStore.profile.state);
    const myCountry = computed(() => profileStore.profile?.country?.label);
    const myProfession = computed(() => profileStore.profile?.profession?.label);
    const mySpecialty = computed(() => profileStore.profile?.professionSpeciality?.label);
    const myPhone = computed(() => profileStore.profile.phoneNumber);
    const isStudent = computed(() => profileStore.profile.studyType === 'STUDENT');
    const isResident = computed(() => profileStore.profile.studyType === 'RESIDENT');
    const graduationTimestamp = computed(() => profileStore.profile.graduationDate);
    const graduationDate = computed(() => {
      const date = new Date(graduationTimestamp.value as unknown as number);
      return date.toLocaleDateString();
    });

    const positionData = ref({});

    const hasContact = computed(() => !!(profile.value.email || profile.value.phone
              || profile.value.street || profile.value.city
              || profile.value.state || profile.value.zipCode));

    const hasEducation = computed(() => {
      if (!profile.value.educations?.length) {
        return false;
      }
      if (profile.value.educations.length > 1) {
        return true;
      }
      return !!(profile.value.educations[0]?.degree || profile.value.educations[0]?.school
      || profile.value.educations[0]?.startDate || profile.value.educations[0]?.endDate);
    });

    const hasWorkplace = computed(() => {
      if (!profile.value?.workplaces?.length) {
        return false;
      }
      if (profile.value.workplaces.length > 1) {
        return true;
      }
      return profile.value.workplaces[0].city || profile.value.workplaces[0].name || profile.value.workplaces[0].state
      || profile.value.workplaces[0].street || profile.value.workplaces[0].zipCode;
    });
    async function fecthUserPublicCases() {
      if (currentPage.value === NAVIGATION_PAGES.PROFILE_MY) {
        driveStore.fetchUserPublicCases(mainStore.user.accountId)
          .then((response) => {
            driveStore.$patch((state) => {
              state.userPublicCases.data = [...response];
            });
          });
      } else {
        driveStore.fetchUserPublicCases(route.params.id)
          .then((response) => {
            driveStore.$patch((state) => {
              state.userPublicCases.data = [...response];
            });
          });
      }
    }

    const speciality = computed(() => mainStore.specialities.find((specialityItem) => specialityItem.value === profile.value.speciality?.value)?.label);

    function dateFormatter(date: Date | null): string {
      if (!date) {
        return 'No information';
      }
      const month = new Date(date).getMonth();
      const year = new Date(date).getFullYear();

      return `${month < 10 ? `0${month}` : month}/${year}`;
    }

    function messagePerson(id: string) {
      if (mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.BETA)) {
        spacesStore.showBetaUserPopUp = true;
        return;
      }
      router.push(`/messages/${id}`);
    }
    function getPos(event) {
      const x = event.clientX;
      const y = event.clientY;
      positionData.value = { x, y };
    }

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      fecthUserPublicCases();
      profileStore.fetchAndReturnProfile();
    });

    return {
      profileStore,
      hasContact,
      isPremium,
      isDeleted,
      casesCreated,
      photosUploaded,
      speciality,
      hasEducation,
      hasWorkplace,
      isMyProfile,
      profile,

      myState,
      myCountry,
      isStudent,
      isResident,
      myProfession,
      mySpecialty,
      myPhone,
      graduationDate,
      graduationTimestamp,

      profileId,
      showPopUp,
      disableButton,
      mainStore,
      publicCasesAmount,
      dateFormatter,
      messagePerson,
      driveStore,
      isPublicCases,
      myProfileById,
      getPos,
      positionData,
    };
  },
});
